import { useEffect } from 'react';

import { tokenService } from '../services/tokenService';
import { sendVisitorData } from '../services/trackingService';
import { getGoogleAnalyticsClientId, getVisits } from '../utils/visitorTracker';

/** Retry failed visits and IDs that appear after GTM/consent finishes loading. */
export const useVisitorTracking = (userId?: string, isImpersonating = false): void => {
  // Keep timer, in-flight guard and cleanup together so account switches cancel the same lifecycle.
  // eslint-disable-next-line max-statements
  useEffect(() => {
    if (!userId || isImpersonating || window.__load8080Analytics === false) return undefined;

    let cancelled = false;
    let sending = false;
    let storedClientId: string | null = null;
    // eslint-disable-next-line max-statements
    const flush = async () => {
      if (sending || cancelled) return;

      const accessToken = tokenService.getAccessToken();
      const isCurrent = () => !cancelled && tokenService.getAccessToken() === accessToken;

      const visits = getVisits();
      const clientId = getGoogleAnalyticsClientId();

      if (!visits.length && (!clientId || clientId === storedClientId)) return;
      sending = true;
      try {
        if (await sendVisitorData(visits, clientId, isCurrent)) storedClientId = clientId;
      } finally {
        sending = false;
      }
    };

    void flush();
    const interval = window.setInterval(flush, 5000);

    window.addEventListener('online', flush);
    window.addEventListener('focus', flush);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('online', flush);
      window.removeEventListener('focus', flush);
    };
  }, [userId, isImpersonating]);
};
